import React, { useEffect, useRef, useState } from 'react';
import { ScanLine, CheckCircle2 } from 'lucide-react';

const DEMO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_5f0a0968-5d67-4938-9bcf-25f8dd12efad/artifacts/yemumfxw_demo-walkthrough.mp4";

// Items to "detect" — each appears in the inventory panel at the given second
const DETECTED_ITEMS = [
  { id: 1, label: 'Sofa (L-shape)', confidence: 96, appearAt: 1.5 },
  { id: 2, label: 'Wall art', confidence: 87, appearAt: 3.2 },
  { id: 3, label: 'Swivel chair', confidence: 91, appearAt: 5.0 },
  { id: 4, label: 'Plant', confidence: 89, appearAt: 7.0 },
  { id: 5, label: 'Piano', confidence: 98, appearAt: 9.0 },
  { id: 6, label: 'Dining table', confidence: 94, appearAt: 11.0 },
  { id: 7, label: 'Bookshelf', confidence: 88, appearAt: 13.0 },
];

// Floating overlay labels on the video itself
const FLOATING_LABELS = [
  { id: 'fl1', label: 'Sofa (L-shape)', confidence: 72, appearAt: 1.5, hideAt: 4.0, top: '58%', left: '18%' },
  { id: 'fl2', label: 'Wall art', confidence: 87, appearAt: 3.2, hideAt: 6.0, top: '14%', left: '55%' },
  { id: 'fl3', label: 'Swivel chair', confidence: 91, appearAt: 5.0, hideAt: 8.0, top: '46%', left: '62%' },
  { id: 'fl4', label: 'Plant', confidence: 89, appearAt: 7.0, hideAt: 10.0, top: '40%', left: '78%' },
];

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const VideoSurveyDemo = () => {
  const videoRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);

  // Track video playback time to drive overlays + inventory list
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTime = () => setCurrentTime(video.currentTime);
    const onLoop = () => setCurrentTime(0);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('seeked', onTime);
    video.addEventListener('ended', onLoop);
    return () => {
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('seeked', onTime);
      video.removeEventListener('ended', onLoop);
    };
  }, []);

  const visibleItems = DETECTED_ITEMS.filter((it) => currentTime >= it.appearAt);
  const visibleFloating = FLOATING_LABELS.filter(
    (f) => currentTime >= f.appearAt && currentTime <= f.hideAt
  );

  return (
    <div className="grid lg:grid-cols-[1.25fr_1fr] gap-6 lg:gap-8" data-testid="video-survey-demo">
      {/* ===== LEFT: Video phone frame with overlays ===== */}
      <div className="relative">
        {/* Subtle glow behind frame */}
        <div className="absolute -inset-6 bg-gradient-to-br from-[#3B43AF]/30 via-amber-500/10 to-emerald-500/10 rounded-[2.5rem] blur-3xl"></div>

        <div className="relative rounded-[1.75rem] overflow-hidden border border-white/10 bg-black shadow-2xl shadow-black/40 aspect-[4/3]">
          <video
            ref={videoRef}
            src={DEMO_VIDEO_URL}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            data-testid="demo-video-player"
          />

          {/* Top bar — LIVE + timer + room indicator */}
          <div className="absolute top-3 left-3 right-3 flex items-center justify-between text-xs font-mono uppercase tracking-wider text-white pointer-events-none">
            <div className="flex items-center gap-2 bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-full">
              <span className="relative flex items-center">
                <span className="absolute w-2 h-2 bg-emerald-400 rounded-full animate-ping opacity-75"></span>
                <span className="relative w-2 h-2 bg-emerald-400 rounded-full"></span>
              </span>
              <span className="font-semibold">LIVE</span>
              <span className="text-white/70">{formatTime(currentTime)}</span>
            </div>
            <div className="bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-full text-white/80">
              LIVING ROOM · 1 OF 5
            </div>
          </div>

          {/* Corner brackets — scanner aesthetic */}
          <CornerBracket position="top-left" />
          <CornerBracket position="top-right" />
          <CornerBracket position="bottom-left" />
          <CornerBracket position="bottom-right" />

          {/* Floating detection labels on the video */}
          {visibleFloating.map((f) => (
            <div
              key={f.id}
              className="absolute pointer-events-none animate-fade-in"
              style={{ top: f.top, left: f.left }}
            >
              <div className="flex items-center gap-2 bg-black/60 backdrop-blur-sm border border-emerald-400/40 px-2 py-1 rounded-md text-xs font-mono text-white whitespace-nowrap shadow-lg">
                <span>{f.label}</span>
                <span className="text-amber-400 font-semibold">{f.confidence}%</span>
              </div>
            </div>
          ))}

          {/* Bottom — TAP TO STOP pill (decorative) */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 pointer-events-none">
            <div className="bg-white/15 backdrop-blur-md border border-white/30 px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider text-white">
              Tap to stop
            </div>
          </div>
        </div>
      </div>

      {/* ===== RIGHT: Verified Inventory panel ===== */}
      <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-5 lg:p-6 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <ScanLine className="w-4 h-4 text-emerald-400" />
            <span className="font-mono text-xs uppercase tracking-wider text-white font-semibold">
              Verified Inventory
            </span>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-wider text-emerald-400/80">
            SYS_ACTIVE
          </span>
        </div>

        {/* Items list */}
        <div className="mt-4 space-y-2.5 min-h-[340px]">
          {visibleItems.length === 0 && (
            <p className="text-xs font-mono uppercase tracking-wider text-white/30 pt-4">
              Awaiting first detection…
            </p>
          )}
          {visibleItems.map((item) => (
            <InventoryRow key={item.id} item={item} />
          ))}
        </div>

        {/* Counter */}
        <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-white/40">
          <span>Items detected</span>
          <span className="text-emerald-400 font-semibold">{visibleItems.length} / {DETECTED_ITEMS.length}</span>
        </div>
      </div>
    </div>
  );
};

const CornerBracket = ({ position }) => {
  const posClass = {
    'top-left': 'top-2 left-2 border-l-2 border-t-2 rounded-tl-lg',
    'top-right': 'top-2 right-2 border-r-2 border-t-2 rounded-tr-lg',
    'bottom-left': 'bottom-2 left-2 border-l-2 border-b-2 rounded-bl-lg',
    'bottom-right': 'bottom-2 right-2 border-r-2 border-b-2 rounded-br-lg',
  }[position];
  return (
    <div
      className={`absolute w-6 h-6 border-emerald-400/70 ${posClass} pointer-events-none`}
      aria-hidden="true"
    />
  );
};

const InventoryRow = ({ item }) => (
  <div
    className="flex items-center justify-between bg-white/[0.04] hover:bg-white/[0.07] border border-white/5 rounded-lg px-3 py-2.5 transition-all animate-slide-up"
    data-testid={`inventory-item-${item.id}`}
  >
    <div className="flex items-center gap-2.5">
      <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
      <span className="text-sm text-white">{item.label}</span>
    </div>
    <span className="text-xs font-mono font-semibold text-amber-400">{item.confidence}%</span>
  </div>
);

export default VideoSurveyDemo;
